import { useEffect, useState } from "react";
import { Loader2, Trash2, MessageCircle, Mail, Phone, GraduationCap, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUSES = [
  { value: "new", label: "New", color: "bg-[#D4EDDB] text-[#1F2547]" },
  { value: "contacted", label: "Contacted", color: "bg-[#C7DCF7] text-[#1F2547]" },
  { value: "enrolled", label: "Enrolled", color: "bg-[#9F87D8] text-white" },
  { value: "closed", label: "Closed", color: "bg-[#EFE9F7] text-[#1F2547]" },
];

function statusInfo(s) {
  return STATUSES.find((x) => x.value === s) || STATUSES[0];
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function updateStatus(id, status) {
    try {
      await api.patch(`/admin/leads/${id}/status`, { status });
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  }

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const counts = {
    all: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    enrolled: leads.filter((l) => l.status === "enrolled").length,
    closed: leads.filter((l) => l.status === "closed").length,
  };

  return (
    <section data-testid="leads-section">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-black tracking-tight text-[#1F2547]">
          Leads
        </h1>
        <p className="text-sm text-[#5B6079] font-medium mt-1">
          Enquiries submitted from the public site. Click WhatsApp to follow up.
        </p>
      </header>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { v: "all", l: "All" },
          { v: "new", l: "New" },
          { v: "contacted", l: "Contacted" },
          { v: "enrolled", l: "Enrolled" },
          { v: "closed", l: "Closed" },
        ].map(({ v, l }) => (
          <button
            key={v}
            data-testid={`leads-filter-${v}`}
            onClick={() => setFilter(v)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
              filter === v
                ? "bg-[#1F2547] text-white"
                : "bg-white text-[#1F2547] hover:bg-[#EFE9F7]"
            }`}
          >
            {l} <span className="ml-1 opacity-70">({counts[v]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#5B6079] font-medium">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-medium">
          {err}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-[#5B6079] font-medium bg-white rounded-2xl p-6 brutal-shadow-sm">
          No leads {filter !== "all" ? `with status "${filter}"` : "yet"}.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((l) => {
            const s = statusInfo(l.status);
            const waText = encodeURIComponent(
              `Hi ${l.name}, this is Percept Academy following up on your enquiry${l.course_title ? ` for ${l.course_title}` : ""}.`,
            );
            const waPhone = (l.phone || "").replace(/[^\d]/g, "");
            const waLink = `https://wa.me/${waPhone}?text=${waText}`;
            return (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className="bg-white rounded-2xl brutal-shadow-sm p-5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-display font-bold text-lg text-[#1F2547]">
                        {l.name}
                      </h3>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${s.color}`}
                      >
                        {s.label}
                      </span>
                      {l.course_title && (
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#EFE9F7] text-[#1F2547]">
                          {l.course_title}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#5B6079] font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" /> {l.phone}
                      </span>
                      {l.email && (
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5" /> {l.email}
                        </span>
                      )}
                      {l.class_or_age && (
                        <span className="inline-flex items-center gap-1.5">
                          <GraduationCap className="w-3.5 h-3.5" /> {l.class_or_age}
                        </span>
                      )}
                      {l.city && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5" /> {l.city}
                        </span>
                      )}
                    </div>
                    {l.message && (
                      <p className="mt-2 text-sm text-[#1F2547] bg-[#EFE9F7] rounded-xl px-3 py-2 font-medium">
                        {l.message}
                      </p>
                    )}
                    <div className="text-xs text-[#5B6079] mt-2 font-medium">
                      Submitted {new Date(l.created_at).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`lead-whatsapp-${l.id}`}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-[#9F87D8] text-white text-sm font-bold rounded-full btn-press"
                    >
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                    <select
                      data-testid={`lead-status-${l.id}`}
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] bg-[#EFE9F7] text-[#1F2547] border-none focus:outline-none"
                    >
                      {STATUSES.map((s2) => (
                        <option key={s2.value} value={s2.value}>
                          {s2.label}
                        </option>
                      ))}
                    </select>
                    <button
                      data-testid={`lead-delete-${l.id}`}
                      onClick={() => remove(l.id)}
                      className="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"
                      aria-label="Delete lead"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
